// Honey Sort — a water-sort / ball-sort puzzle, honey-jar themed.
//
// Each jar holds up to 4 stacked honey layers. Pour the top run of a color from
// one jar onto an empty jar or onto a matching top layer, until every non-empty
// jar holds a single colour. A large "super jar" acts as an overflow buffer.
//
// Levels are DETERMINISTIC from the level number, so "Reset" regenerates the
// exact same board and progress can resume across sessions.
//
// Design (from the brief):
//   • colours: start at 4, +1 every 20 levels (capped at the palette size, 8).
//   • jars:    3 filled jars per colour  →  12 at level 1, grows with colours.
//   • super jar capacity: starts at 12, loses a layer every 20 levels, min 4.
//   • level complete when every filled jar is a single colour.
//
// Solvability is guaranteed by generating each board as a backward walk from the
// solved state (each step is the inverse of a legal pour), so a legal solution
// always exists — no unsolvable boards are ever handed to the player.

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Warm, coordinated — but clearly distinguishable — honey tones. `fill` is the
// honey colour; `edge` is the slightly darker separator between layers.
export const HONEY_COLORS = [
  { name: "Amber", fill: "#F6A821", edge: "#C77F12" },
  { name: "Gold", fill: "#F4CE4B", edge: "#C9A227" },
  { name: "Clover", fill: "#8FB63C", edge: "#5E7D22" },
  { name: "Rose", fill: "#E86A5C", edge: "#B23B2E" },
  { name: "Plum", fill: "#9B5DE5", edge: "#6A32B0" },
  { name: "Teal", fill: "#3CC6C4", edge: "#1E8180" },
  { name: "Cocoa", fill: "#A9631F", edge: "#7A4111" },
  { name: "Sky", fill: "#5AA9E6", edge: "#2E6FA6" },
];

export const MAX_COLORS = HONEY_COLORS.length; // 8
export const REGULAR_CAPACITY = 4;
const JARS_PER_COLOR = 3;

const tier = (level) => Math.floor((Math.max(1, level | 0) - 1) / 20);

export function colorsForLevel(level) {
  return Math.min(4 + tier(level), MAX_COLORS);
}
export function jarsForLevel(level) {
  return colorsForLevel(level) * JARS_PER_COLOR; // 12 at level 1
}
export function superCapacityForLevel(level) {
  return Math.max(12 - tier(level), REGULAR_CAPACITY); // 12 → 4
}

// Everything the UI needs to lay a level out. `capacities[i]` is the max layers
// for jar i; the last jar (index === superIndex) is the super buffer.
export function levelConfig(level) {
  const colors = colorsForLevel(level);
  const filledJars = jarsForLevel(level);
  const superCap = superCapacityForLevel(level);
  const capacities = [];
  for (let i = 0; i < filledJars; i++) capacities.push(REGULAR_CAPACITY);
  capacities.push(superCap);
  return {
    level: Math.max(1, level | 0),
    colors,
    filledJars,
    superCap,
    superIndex: filledJars,
    regularCap: REGULAR_CAPACITY,
    capacities,
  };
}

/** Colour + length of the contiguous run at the top of a jar. */
export function topRun(jar) {
  if (jar.length === 0) return { color: -1, len: 0 };
  const color = jar[jar.length - 1];
  let len = 1;
  for (let i = jar.length - 2; i >= 0 && jar[i] === color; i--) len++;
  return { color, len };
}

const shuffledIndices = (n, rng) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// One backward step: move a single top layer of colour c from `src` to a `dst`
// whose top is a DIFFERENT colour (or empty). The forward pour dst→src that
// undoes this is legal (dst top = c, and src still ends empty-or-top-c), so the
// resulting board can always reach the solved state.
//
// `preferSuperSrc` biases unloading the super buffer; `avoidSuperDst` keeps the
// buffer as empty as possible so it reads as a fresh overflow jar at the start.
function backwardStep(jars, capacities, superIndex, rng, { preferSuperSrc, avoidSuperDst } = {}) {
  const order = shuffledIndices(jars.length, rng);
  if (preferSuperSrc) order.sort((a, b) => (b === superIndex) - (a === superIndex));

  for (const src of order) {
    const jar = jars[src];
    if (jar.length === 0) continue;
    const { color: c, len } = topRun(jar);
    // After removing one layer, src must stay empty or keep colour c on top.
    if (!(len >= 2 || jar.length === 1)) continue;

    const regularDsts = [];
    const superDsts = [];
    for (let d = 0; d < jars.length; d++) {
      if (d === src) continue;
      if (jars[d].length >= capacities[d]) continue;
      const dtop = jars[d].length ? jars[d][jars[d].length - 1] : -1;
      if (dtop === c) continue; // need a different top so the undo run is exactly 1
      (d === superIndex ? superDsts : regularDsts).push(d);
    }

    let pool = regularDsts;
    if (pool.length === 0 || (!avoidSuperDst && superDsts.length && rng() < 0.15)) {
      pool = regularDsts.length ? regularDsts.concat(superDsts) : superDsts;
    }
    if (pool.length === 0) continue;

    const dst = pool[Math.floor(rng() * pool.length)];
    jar.pop();
    jars[dst].push(c);
    return true;
  }
  return false;
}

export function isSolved(jars) {
  for (const jar of jars) {
    if (jar.length === 0) continue;
    for (let i = 1; i < jar.length; i++) if (jar[i] !== jar[0]) return false;
  }
  return true;
}

const canonical = (board) => board.map((j) => j.join(",")).sort().join("|");

// Depth-first solver with canonical de-duplication, aggressive pruning, move
// ordering and a node budget. Returns true only when it actually finds a legal
// solution — used to verify a randomly-filled board is solvable before handing
// it to the player. `NODE_CAP` also bounds worst-case generation time.
function isSolvable(board, capacities) {
  const NODE_CAP = 40000;
  const seen = new Set();
  const start = board.map((j) => j.slice());
  seen.add(canonical(start));
  const stack = [start];
  let nodes = 0;
  while (stack.length) {
    const b = stack.pop();
    if (isSolved(b)) return true;
    if (++nodes > NODE_CAP) return false; // give up → caller reshuffles
    const moves = [];
    for (let f = 0; f < b.length; f++) {
      const jar = b[f];
      if (jar.length === 0) continue;
      const { color, len } = topRun(jar);
      if (len === jar.length && jar.length === capacities[f]) continue; // complete jar
      const srcUniform = len === jar.length;
      let usedEmpty = false;
      for (let t = 0; t < b.length; t++) {
        if (t === f) continue;
        const dstLen = b[t].length;
        if (dstLen === 0) {
          if (srcUniform) continue; // pointless whole-stack relocation
          if (usedEmpty) continue; // empty jars are interchangeable
          usedEmpty = true;
        } else if (b[t][dstLen - 1] !== color) {
          continue; // colour mismatch
        }
        const nx = pour(b, capacities, f, t);
        if (!nx) continue;
        const key = canonical(nx);
        if (seen.has(key)) continue;
        seen.add(key);
        moves.push([nx, dstLen === 0 ? 0 : 1]);
      }
    }
    moves.sort((a, z) => a[1] - z[1]); // consolidating moves explored first
    for (const [nx] of moves) stack.push(nx);
  }
  return false;
}

// Random full jars + an EMPTY buffer. Each colour appears 3×4 = 12 times.
function randomFillBoard(cfg, rng) {
  const bag = [];
  for (let c = 0; c < cfg.colors; c++) {
    for (let k = 0; k < JARS_PER_COLOR * REGULAR_CAPACITY; k++) bag.push(c);
  }
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  const jars = [];
  for (let i = 0; i < cfg.filledJars; i++) {
    jars.push(bag.slice(i * REGULAR_CAPACITY, (i + 1) * REGULAR_CAPACITY));
  }
  jars.push([]); // super buffer starts EMPTY
  return jars;
}

// Guaranteed-solvable fallback: build backward from the solved state. The buffer
// may hold a few layers here; only used if the verify path can't find a solvable
// full-jars board in budget (the very hardest tiers).
function backwardGenerate(cfg, rng) {
  const { colors, superIndex, capacities } = cfg;
  const jars = [];
  for (let c = 0; c < colors; c++) {
    for (let k = 0; k < JARS_PER_COLOR; k++) jars.push([c, c, c, c]);
  }
  jars.push([]);
  const steps = Math.max(700, 160 * cfg.filledJars);
  for (let s = 0; s < steps; s++) {
    const drain = jars[superIndex].length > 0 && s % 2 === 0;
    backwardStep(jars, capacities, superIndex, rng, { avoidSuperDst: true, preferSuperSrc: drain });
  }
  for (let s = 0; s < 1500 && jars[superIndex].length > 0; s++) {
    if (!backwardStep(jars, capacities, superIndex, rng, { preferSuperSrc: true, avoidSuperDst: true })) break;
  }
  let guard = 0;
  while (isSolved(jars) && guard++ < 50) {
    backwardStep(jars, capacities, superIndex, rng, { avoidSuperDst: true });
  }
  return jars;
}

/**
 * Build a solvable, mixed board for `level`: full jars, EMPTY buffer.
 * Returns `number[][]` (bottom→top colour indices); the last jar is the super
 * buffer. Deterministic per level, so Reset regenerates the same board.
 */
export function generateLevel(level) {
  const cfg = levelConfig(level);
  const rng = mulberry32(((cfg.level + 1) * 2654435761) >>> 0);
  for (let attempt = 0; attempt < 150; attempt++) {
    const jars = randomFillBoard(cfg, rng);
    if (!isSolved(jars) && isSolvable(jars, cfg.capacities)) return jars;
  }
  return backwardGenerate(cfg, rng);
}

/**
 * Attempt a legal pour from `from` → `to`. Returns a NEW board on success, or
 * `null` if the move is illegal. Moves the whole top same-colour run, capped by
 * the destination's free space.
 */
export function pour(jars, capacities, from, to) {
  if (from === to) return null;
  const src = jars[from];
  if (src.length === 0) return null;
  const { color, len } = topRun(src);
  const dst = jars[to];
  const space = capacities[to] - dst.length;
  if (space <= 0) return null;
  if (dst.length > 0 && dst[dst.length - 1] !== color) return null;
  const m = Math.min(len, space);
  const next = jars.map((a) => a.slice());
  for (let k = 0; k < m; k++) {
    next[from].pop();
    next[to].push(color);
  }
  return next;
}

/** Would a pour from `from` land anywhere legal? (for highlighting / hints) */
export function hasAnyMove(jars, capacities) {
  for (let f = 0; f < jars.length; f++) {
    for (let t = 0; t < jars.length; t++) {
      if (pour(jars, capacities, f, t)) return true;
    }
  }
  return false;
}
